import { makeAutoObservable } from "mobx";
import menuItems, { MenuItem, MENU_ITEM_LABEL_KEY, MENU_ITEM_TYPE } from "../components/layouts/app-layout/app-sidebar/menu-items";
import { PERMISSION } from "../models/PermissionsTypes";
import { PROFILE_CAPABILITIES } from "../models/ProfileCapabilities";
import dashboardSettingsStore from "./dashboard-settings-store";

/**
 * Store responsible for parsing and transforming menu items based on permissions and settings.
 * Filters unavailable items and collapses single-child parents into single menu items.
 */
class MenuItemsStore {

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    private isMenuItemAvailable(pageLabelKey: MENU_ITEM_LABEL_KEY): boolean {
        if (dashboardSettingsStore.isDashboardLocked && ![
            MENU_ITEM_LABEL_KEY.HOME, 
            MENU_ITEM_LABEL_KEY.RETURN_COVERAGE, 
            MENU_ITEM_LABEL_KEY.TROUBLESHOOTING
        ].includes(pageLabelKey)) {
            return false;
        }
        
        switch (pageLabelKey) {
            case MENU_ITEM_LABEL_KEY.HOME:
                return dashboardSettingsStore.hasPermission(PERMISSION.VIEW_HOME_PAGE);
            
            case MENU_ITEM_LABEL_KEY.RETURNS_AND_EXCHANGES:
            case MENU_ITEM_LABEL_KEY.MANAGE_RETURNS:
                return dashboardSettingsStore.hasPermission(PERMISSION.READ_RMA);
                
            case MENU_ITEM_LABEL_KEY.RETURN_COVERAGE:
                return dashboardSettingsStore.isCoveragePageShown;
                
            case MENU_ITEM_LABEL_KEY.SHIP_BY_RETURNGO:
                return dashboardSettingsStore.isSBRPromotionPageShown;
            
            case MENU_ITEM_LABEL_KEY.SETTINGS:
            case MENU_ITEM_LABEL_KEY.GENERAL:
            case MENU_ITEM_LABEL_KEY.LANGUAGES:
            case MENU_ITEM_LABEL_KEY.RETURN_REASONS:
            case MENU_ITEM_LABEL_KEY.RESOLUTIONS:
            case MENU_ITEM_LABEL_KEY.RETURN_METHODS:
            case MENU_ITEM_LABEL_KEY.RETURN_POLICY:
            case MENU_ITEM_LABEL_KEY.EMAIL:
            case MENU_ITEM_LABEL_KEY.CONTENT:
            case MENU_ITEM_LABEL_KEY.LOOK_AND_FEEL:
            case MENU_ITEM_LABEL_KEY.NOTIFICATIONS:
            case MENU_ITEM_LABEL_KEY.INTEGRATIONS:
                return dashboardSettingsStore.hasPermission(PERMISSION.READ_SETTINGS);

            case MENU_ITEM_LABEL_KEY.API:
                return dashboardSettingsStore.isExternalApiSupported && dashboardSettingsStore.hasPermission(PERMISSION.READ_SETTINGS);

            case MENU_ITEM_LABEL_KEY.STOREFRONT:
                const storefrontSettingsCapability = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.STOREFRONT_SETTINGS);
                return storefrontSettingsCapability.isSupported &&  dashboardSettingsStore.hasPermission(PERMISSION.READ_SETTINGS);
            
            case MENU_ITEM_LABEL_KEY.PORTAL:
                return dashboardSettingsStore.isCustomizationSupported;

            case MENU_ITEM_LABEL_KEY.ANALYTICS:
            case MENU_ITEM_LABEL_KEY.RETURNS:
            case MENU_ITEM_LABEL_KEY.SESSIONS:
            case MENU_ITEM_LABEL_KEY.TRANSACTIONS:
            case MENU_ITEM_LABEL_KEY.SHIPMENTS:
                return dashboardSettingsStore.hasPermission(PERMISSION.VIEW_ANALYTICS_PAGE);

            case MENU_ITEM_LABEL_KEY.CREDITS:
                return dashboardSettingsStore.isAnalyticsCreditsSupported && dashboardSettingsStore.hasPermission(PERMISSION.VIEW_ANALYTICS_PAGE);
            
            case MENU_ITEM_LABEL_KEY.ORDERS:
                return dashboardSettingsStore.isAnalyticsOrdersSupported && dashboardSettingsStore.hasPermission(PERMISSION.ALL);

            case MENU_ITEM_LABEL_KEY.BACK_AS_ADMINISTRATOR:
                return dashboardSettingsStore.isSuperAdmin;

            case MENU_ITEM_LABEL_KEY.TROUBLESHOOTING:
                return dashboardSettingsStore.isSuperAdmin || dashboardSettingsStore.isSuperAdminDashboard;

            case MENU_ITEM_LABEL_KEY.HEALTH_PAGE:
                return dashboardSettingsStore.isSuperAdmin || dashboardSettingsStore.isSuperAdminDashboard;

            default:
                return true;
        }
    }

    /**
     * Filters menu items recursively to return only those that are available based on permissions/visibility.
     * Removes items that are not available and removes parent items if all children are filtered out.
     */
    private _filterAvailableItems(menuItems: MenuItem[]): MenuItem[] {
        return menuItems
            .map(item => {
                if (item.label_key && !this.isMenuItemAvailable(item.label_key)) {
                    return null;
                }
                if (Array.isArray(item.items)) {
                    const filteredNestedItems = this._filterAvailableItems(item.items);
                    if (filteredNestedItems.length === 0) {
                        return null;
                    }
                    return { ...item, items: filteredNestedItems };
                }
                return item;
            })
            .filter((item): item is MenuItem => item !== null);
    }

    /**
     * Determines if a menu item with children should be rendered as a single item instead of a collapsible.
     * This happens when there's only one available child with a valid route.
     */
    private _shouldRenderAsSingleItem(items: MenuItem[] | undefined): boolean {
        return Array.isArray(items) && items.length === 1 && Boolean(items[0]?.to);
    }

    /**
     * Determines the type a menu item should have when collapsed to a single child.
     * Top-level collapsibles become MAIN_MENU_ITEM, nested items preserve their original type.
     */
    private _getCollapsedMenuItemType(originalType: MENU_ITEM_TYPE | undefined): MENU_ITEM_TYPE | undefined {
        if (originalType === MENU_ITEM_TYPE.TOP_LEVEL_COLLAPSIBLE) {
            return MENU_ITEM_TYPE.MAIN_MENU_ITEM;
        }
        return originalType;
    }

    /**
     * Collapses a menu item with children, applying single-child collapsing logic.
     * When a parent has only one child, uses the child's route and adjusts the type accordingly.
     * Returns the item with items set to undefined when collapsing to a single item, otherwise returns the item with updated children.
     */
    private _collapseMenuItemWithChildren(
        item: MenuItem,
        collapsedChildren: MenuItem[]
    ): MenuItem {
        const renderAsSingleItem = this._shouldRenderAsSingleItem(collapsedChildren);
        
        if (!renderAsSingleItem) {
            return {
                ...item,
                items: collapsedChildren
            } as MenuItem;
        }

        const childItem = collapsedChildren[0];
        
        return {
            ...item,
            to: childItem.to,
            type: this._getCollapsedMenuItemType(item.type),
            items: undefined
        } as MenuItem;
    }

    private _collapseSingleChildItems(menuItems: MenuItem[]): MenuItem[] {
        return menuItems.map(item => {
            if (Array.isArray(item.items)) {
                const collapsedChildren = this._collapseSingleChildItems(item.items);
                return this._collapseMenuItemWithChildren(item, collapsedChildren);
            }
            return item;
        });
    }

    /**
     * Computed getter that returns parsed menu items with filtering and collapsing applied.
     * Automatically updates when dashboard settings or permissions change.
     */
    get parsedMenuItems(): MenuItem[] {
        const filteredItems = this._filterAvailableItems(menuItems);
        return this._collapseSingleChildItems(filteredItems);
    }
}

export default MenuItemsStore;
